/**
 * PermissionGate — asks the browser whether a stored handle is still usable.
 * Wraps queryPermission / requestPermission and nothing else.
 */

export type PermissionOutcome = 'granted' | 'denied' | 'prompt';

type Permissioned = FileSystemHandle & {
  queryPermission?: (opts: { mode: 'read' | 'readwrite' }) => Promise<PermissionState>;
  requestPermission?: (opts: { mode: 'read' | 'readwrite' }) => Promise<PermissionState>;
};

export class PermissionGate {
  /** Current state, without showing the user anything. */
  async check(
    handle: FileSystemHandle,
    mode: 'read' | 'readwrite' = 'read',
  ): Promise<PermissionOutcome> {
    const target = handle as Permissioned;
    if (!target.queryPermission) return 'granted'; // browser predates the API
    return (await target.queryPermission({ mode })) as PermissionOutcome;
  }

  /**
   * Ask for access, prompting if needed.
   * Must be called from a user gesture or the browser rejects it outright.
   */
  async request(
    handle: FileSystemHandle,
    mode: 'read' | 'readwrite' = 'read',
  ): Promise<PermissionOutcome> {
    const target = handle as Permissioned;
    if (!target.requestPermission) return 'granted';

    if ((await this.check(handle, mode)) === 'granted') return 'granted';
    return (await target.requestPermission({ mode })) as PermissionOutcome;
  }
}
